/**
 * Site → bot attribution handoff (docs/marketing/attribution.md, «The website»).
 *
 * Marketing links to the site carry `?utm_source=<code>`. The site's own bot links use
 * generic payloads (`?start=site`, `?start=card_<slug>`), so without this a visitor who came
 * from a channel and then starts the bot would be recorded with `referral_code=site` and the
 * channel's code would be lost. Here we:
 *  1. remember the first valid `utm_source` (first touch wins, 30-day expiry, localStorage);
 *  2. on click of a bot deep link with a generic site payload, swap `start=` for that code.
 *
 * Browser-only: call `installSiteAttribution()` from `onMounted` (never during setup) — the site
 * is prerendered with vite-ssg, and the SSR output must stay identical (no hydration mismatch).
 * The href is only rewritten in the DOM at click time, after hydration.
 */
import { TELEGRAM_BOT_BASE_URL } from './constants';

const STORAGE_KEY = 'tarot.site_ref';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** Same rules as the admin's codes (docs/marketing/attribution.md → «Code rules»). */
const CODE_RE = /^[a-z0-9_]{2,40}$/;

/**
 * Start payloads the bot routes elsewhere (core auth / account-link / invite handlers).
 * A code with one of these prefixes would never be stored as a referral, so drop it.
 */
const RESERVED_PREFIXES = ['auth_', 'link_', 'i_'];

/** The site's generic payloads — the only ones we replace. */
const isGenericSitePayload = (start: string): boolean => start === 'site' || start.startsWith('card_');

export const isValidAttributionCode = (code: string): boolean =>
  CODE_RE.test(code) && !RESERVED_PREFIXES.some((p) => code.startsWith(p));

interface StoredRef {
  code: string;
  ts: number;
}

const getStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null; // storage blocked (privacy mode / sandboxed iframe)
  }
};

/** The stored code, or null if none / expired / malformed (expired entries are cleared). */
export function readStoredCode(now = Date.now()): string | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredRef>;
    if (
      typeof parsed.code === 'string' &&
      typeof parsed.ts === 'number' &&
      isValidAttributionCode(parsed.code) &&
      now - parsed.ts < TTL_MS &&
      parsed.ts <= now
    ) {
      return parsed.code;
    }
    storage.removeItem(STORAGE_KEY);
  } catch {
    // malformed JSON or storage error — treat as no code
  }
  return null;
}

/** First touch wins: store `utm_source` from `search` only if valid and nothing (live) is stored. */
export function captureUtmSource(search: string, now = Date.now()): void {
  const storage = getStorage();
  if (!storage) return;
  const code = new URLSearchParams(search).get('utm_source');
  if (!code || !isValidAttributionCode(code)) return;
  if (readStoredCode(now)) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify({ code, ts: now } satisfies StoredRef));
  } catch {
    // quota / blocked — attribution is best-effort
  }
}

const normalizePath = (path: string): string => path.replace(/\/+$/, '').toLowerCase();

/**
 * If `href` is a bot deep link (`<bot base>?start=site|card_*`, bot name in any case),
 * return it with `start=<code>`; otherwise null (startapp / Mini App / other links untouched).
 */
export function rewriteBotHref(href: string, code: string, botBaseUrl = TELEGRAM_BOT_BASE_URL): string | null {
  let url: URL;
  let base: URL;
  try {
    url = new URL(href);
    base = new URL(botBaseUrl);
  } catch {
    return null;
  }
  if (url.host.toLowerCase() !== base.host.toLowerCase()) return null;
  if (normalizePath(url.pathname) !== normalizePath(base.pathname)) return null;
  const start = url.searchParams.get('start');
  if (start === null || !isGenericSitePayload(start)) return null;
  url.searchParams.set('start', code);
  return url.toString();
}

function onLinkActivate(event: Event): void {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  const code = readStoredCode();
  if (!code) return;
  const next = rewriteBotHref(anchor.href, code);
  if (next) anchor.href = next;
}

let installed = false;

/**
 * Capture the landing `utm_source` and install one delegated listener on `document`
 * (idempotent: SiteLayout can remount when the shell switches, the listener stays single).
 * `click` covers taps, Enter and modifier-clicks; `auxclick` middle-click; `contextmenu`
 * "copy link" / "open in new tab" from the context menu.
 */
export function installSiteAttribution(): void {
  if (typeof window === 'undefined') return;
  captureUtmSource(window.location.search);
  if (installed) return;
  installed = true;
  for (const type of ['click', 'auxclick', 'contextmenu'] as const) {
    document.addEventListener(type, onLinkActivate, { capture: true });
  }
}
